import { ReactNode } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { CalendarShortcut } from '../components/DatePicker/Calendar';
import { ISelectMeta, ITextMeta } from '../data-models/field3.data-model';
import { FDMap } from '../util/data-structure/FDMap';
import { IDisplayField } from './display-field.view-model';

export type FormVariant = 'form' | 'form-inline' | 'legacy-form';
const DefaultFormVariant: FormVariant = 'form';

export interface IFormField<T> extends IDisplayField<T> {
  autoFocus?: boolean;
  disableClearable?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  required?: boolean;
  /** Extra react-hook-form rules (pattern, validate, ...) merged over the default required rule. */
  rules?: UseControllerProps['rules'];
  /** Select the input text on focus. Omit to keep MUI's default (true for non-freeSolo selects). */
  selectOnFocus?: boolean;
  variant?: FormVariant;
}

export interface IForm<T = unknown> {
  data?: T;
  disabled?: boolean;
  fields: FDMap<string, IFormField<unknown>>;
  title?: string;
  variant?: FormVariant;
}

/** rendererMeta for RendererType.formula. */
export interface IFormFieldFormulaMeta {
  /** Key of the formula field being edited — kept out of its own vocabulary so it can't reference itself. */
  excludeKey?: string;
}

export interface IFormFieldTextMeta extends ITextMeta {
  rows?: number;
  maxRows?: number;
  multiline?: boolean;
}

export interface IFormFieldUploadMeta {
  accept?: string;
  companyId: number;
  maxFileSizeMb?: number;
  multiple?: boolean;
}

export interface IDateFieldFormSettings {
  min: string | null; // ISO date
  max: string | null; // ISO date
  presets: CalendarShortcut[] | null;
}

export interface IFormFieldSelectMeta<T> extends ISelectMeta<T> {
  allowCustomAdd?: boolean;
  /**
   * `two-line` puts the choice's `caption` on a second line under the label, for options
   * whose label alone does not say enough to choose between them. It also widens the
   * search to the secondary label, caption and value, since those lines are the reason
   * the option is findable at all.
   */
  optionLayout?: 'single-line' | 'two-line';
  /**
   * Caps the open list at this many options. Applied after the search, so every option is
   * still found by typing; for lists too long to mount whole.
   */
  optionLimit?: number;
  /** Shows the list's loading text while its options are still arriving. */
  loading?: boolean;
  /** Rendered after the value, outside the autocomplete's own (hover-only) adornments. */
  endAdornment?: ReactNode;
  /**
   * Rendered inside the input, right-aligned ahead of the autocomplete's own controls, for a
   * short annotation of the current value. Unlike `endAdornment` it takes no width from the
   * field itself, so a column of these keeps every input the same size.
   */
  inputEndAdornment?: ReactNode;
  onCustomAdd?: (inputValue?: string) => void;
  actionMeta?: {
    actionLabel?: string;
    helperText?: string;
    onAction?: (inputValue?: string) => void;
    onDelete?: (value: T) => void;
  };
}

export interface IFormFieldFreeLabelsMeta extends ISelectMeta<string> {
  maxTags?: number;
}

export function createFormField<T>(data: IFormField<T>): IFormField<T> {
  const { key, label, renderer, ...rest } = data;
  return {
    key,
    renderer,
    label: label ?? key ?? 'label',
    variant: 'form',
    ...rest,
  };
}

export function createFormFromFieldsArray<T>(
  fields: IFormField<unknown>[],
  formConfig: Omit<IForm<T>, 'fields'> = {}
): IForm<T> {
  return {
    variant: DefaultFormVariant,
    fields: FDMap.fromArray(fields, 'key'),
    ...formConfig,
  };
}

export function createForm<T>(overrides?: Partial<IForm<T>>): IForm<T> {
  return {
    data: {} as T,
    fields: new FDMap<string, IFormField<unknown>>(),
    title: '',
    ...overrides,
  };
}
